import { getErrorMessage } from '../../utils/errorMessage';
import { useCallback, useEffect, useRef, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  BookOpen,
  Check,
  ChevronLeft,
  ChevronRight,
  GraduationCap,
  CircleHelp,
  Home,
  Images,
  LayoutGrid,
  // LibraryBig,
  Languages,
  LoaderCircle,
  LogOut,
  Monitor,
  Moon,
  Settings,
  Sun,
} from 'lucide-react';
import api from '../../utils/api';
import UserAvatar from './UserAvatar';
import AnimatedBrandText from '../home/AnimatedBrandText';
import { SystemMessageTrigger } from './SystemMessageCenter';
import { getTeams, switchTeam, teamApiError } from '../account/teamApi';
import { useActiveTeamBranding } from '../branding/teamBrandingEntitlement';

const NAV_ITEMS = [
  { to: '/home', icon: Home, label: '首页', labelEn: 'Home' },
  { to: '/writing', icon: BookOpen, label: '写剧本', labelEn: 'Write' },
  { to: '/canvas', icon: LayoutGrid, label: '无限画布', labelEn: 'Canvas' },
  { to: '/assets', icon: Images, label: '资产中心', labelEn: 'Assets' },
  // 模板库功能和路由继续保留，当前只暂时隐藏用户侧入口。
  // { to: '/templates', icon: LibraryBig, label: '模板库', labelEn: 'Templates' },
  { to: '/learn', icon: GraduationCap, label: '学习与案例', labelEn: 'Learn & Cases' },
];

const HELP_ITEM = { to: '/help', icon: CircleHelp, label: '帮助中心', labelEn: 'Help Center' };
const SETTINGS_ITEM = { icon: Settings, label: '设置', labelEn: 'Settings' };

const THEME_OPTIONS = [
  { value: 'light', icon: Sun, label: '浅色', labelEn: 'Light' },
  { value: 'dark', icon: Moon, label: '深色', labelEn: 'Dark' },
  { value: 'system', icon: Monitor, label: '跟随系统', labelEn: 'System' },
];

const LANGUAGE_OPTIONS = [
  { value: 'zh-CN', label: '简体中文', railLabel: '中文' },
  { value: 'en-US', label: 'English', railLabel: 'English' },
];

function initialSidebarExpanded() {
  try {
    return localStorage.getItem('vnext-sidebar-expanded') === 'true';
  } catch {
    return false;
  }
}

function TeamBrandLogo({ team, expanded }) {
  const [imageFailed, setImageFailed] = useState(false);
  const name = team?.brand_name || team?.name || '团队';
  const logo = expanded ? (team?.brand_wordmark_url || team?.brand_logo_url) : team?.brand_logo_url;

  useEffect(() => { setImageFailed(false); }, [logo]);

  if (!logo || imageFailed) {
    return <span className={`flex h-10 min-w-0 items-center justify-center overflow-hidden font-semibold ${expanded ? 'w-[168px] text-sm' : 'w-10 text-lg'}`} data-testid="team-brand-logo" aria-hidden="true"><span className="truncate">{expanded ? name : name.charAt(0)}</span></span>;
  }

  return (
    <span
      className={`flex h-10 min-w-0 items-center justify-center overflow-hidden transition-[width] duration-300 ${expanded ? 'w-[168px]' : 'w-10'}`}
      data-testid="team-brand-logo"
      aria-hidden="true"
    >
      <img
        src={logo}
        alt={`${name}团队 Logo`}
        className="block max-h-9 max-w-full object-contain"
        onError={() => setImageFailed(true)}
      />
    </span>
  );
}

function RailLink({ item, expanded = false, mobile = false }) {
  const Icon = item.icon;

  return (
    <NavLink
      to={item.to}
      end={item.to === '/home' || item.to === '/prototype/vnext'}
      title={!expanded ? item.label : undefined}
      aria-label={item.label}
      className={({ isActive }) => (
        mobile
          ? `relative flex min-w-0 flex-1 flex-col items-center gap-1 rounded-xl px-1 py-2 text-[10px] transition ${
              isActive
                ? 'bg-black/[0.055] text-[#29242c] dark:bg-white/[0.08] dark:text-white'
                : 'text-stone-400 hover:text-stone-700 dark:text-white/38 dark:hover:text-white/75'
            }`
          : `group relative flex h-11 w-full items-center rounded-[14px] border px-3 transition ${
              isActive
                ? 'border-black/[0.055] bg-black/[0.055] text-[#29242c] shadow-[0_10px_28px_-24px_rgba(40,35,45,.45)] dark:border-white/[0.07] dark:bg-white/[0.075] dark:text-white'
                : 'border-transparent text-stone-500 hover:bg-black/[0.035] hover:text-stone-800 dark:text-white/42 dark:hover:bg-white/[0.045] dark:hover:text-white/78'
            }`
      )}
    >
      {({ isActive }) => (
        <>
          {!mobile && isActive && (
            <span className="absolute -left-1 h-5 w-[2px] rounded-full bg-[#84739f] dark:bg-[#b9a7e7]" aria-hidden="true" />
          )}
          <span className="flex h-5 w-5 shrink-0 items-center justify-center">
            <Icon size={mobile ? 18 : 18} strokeWidth={1.75} />
          </span>
          {mobile ? (
            <span className="truncate">{item.label}</span>
          ) : (
            <span className={`overflow-hidden whitespace-nowrap text-[12px] font-medium transition-all duration-300 ${
              expanded ? 'ml-3 w-[140px] translate-x-0 opacity-100' : 'ml-0 w-0 -translate-x-1 opacity-0'
            }`}>
              {item.label}
            </span>
          )}
          {!mobile && !expanded && (
            <span className="pointer-events-none absolute left-full z-50 ml-3 whitespace-nowrap rounded-xl border border-black/[0.08] bg-[#fffdfa] px-2.5 py-1.5 text-[11px] font-medium text-stone-700 opacity-0 shadow-xl transition group-hover:opacity-100 dark:border-white/[0.08] dark:bg-[#211e25] dark:text-white">
              {item.label}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}

function ThemeMenu({ theme, setTheme, expanded, english = false }) {
  const [open, setOpen] = useState(false);
  const active = THEME_OPTIONS.find(option => option.value === theme) || THEME_OPTIONS[2];
  const ActiveIcon = active.icon;
  const activeLabel = english ? active.labelEn : active.label;

  return (
    <div className="relative w-full">
      <button
        type="button"
        onClick={() => setOpen(value => !value)}
        className="flex h-10 w-full items-center rounded-[13px] px-3 text-left text-stone-500 transition hover:bg-black/[0.035] hover:text-stone-800 dark:text-white/42 dark:hover:bg-white/[0.045] dark:hover:text-white/78"
        title={!expanded ? activeLabel : undefined}
        aria-label={english ? `Theme: ${activeLabel}` : `主题：${activeLabel}`}
        aria-expanded={open}
      >
        <span className="flex h-5 w-5 shrink-0 items-center justify-center">
          <ActiveIcon size={17} />
        </span>
        <span className={`overflow-hidden whitespace-nowrap text-[12px] font-medium transition-all duration-300 ${
          expanded ? 'ml-3 w-[140px] opacity-100' : 'ml-0 w-0 opacity-0'
        }`}>
          {activeLabel}
        </span>
      </button>
      {open && (
        <>
          <button
            type="button"
            className="fixed inset-0 z-40 cursor-default"
            aria-label={english ? 'Close theme menu' : '关闭主题菜单'}
            onClick={() => setOpen(false)}
          />
          <div className="absolute bottom-0 left-full z-50 ml-3 w-36 overflow-hidden rounded-2xl border border-black/[0.08] bg-[#fffdfa] p-1.5 shadow-2xl dark:border-white/[0.08] dark:bg-[#211e25]">
            {THEME_OPTIONS.map(option => {
              const Icon = option.icon;
              return (
                <button
                  type="button"
                  key={option.value}
                  onClick={() => {
                    setTheme(option.value);
                    setOpen(false);
                  }}
                  className={`flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-[12px] transition ${
                    theme === option.value
                      ? 'bg-black/[0.06] text-stone-900 dark:bg-white/[0.1] dark:text-white'
                      : 'text-stone-500 hover:bg-black/[0.035] hover:text-stone-900 dark:text-white/55 dark:hover:bg-white/[0.06] dark:hover:text-white'
                  }`}
                >
                  <Icon size={14} />
                  {english ? option.labelEn : option.label}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function LanguageMenu({ language, setLanguage, expanded }) {
  const [open, setOpen] = useState(false);
  const active = LANGUAGE_OPTIONS.find(option => option.value === language) || LANGUAGE_OPTIONS[0];

  return (
    <div className="relative w-full">
      <button
        type="button"
        onClick={() => setOpen(value => !value)}
        className="flex h-10 w-full items-center rounded-[13px] px-3 text-left text-stone-500 transition hover:bg-black/[0.035] hover:text-stone-800 dark:text-white/42 dark:hover:bg-white/[0.045] dark:hover:text-white/78"
        title={!expanded ? active.label : undefined}
        aria-label={`语言：${active.label}`}
        aria-expanded={open}
      >
        <span className="flex h-5 w-5 shrink-0 items-center justify-center">
          <Languages size={17} />
        </span>
        <span className={`overflow-hidden whitespace-nowrap text-[12px] font-medium transition-all duration-300 ${
          expanded ? 'ml-3 w-[140px] opacity-100' : 'ml-0 w-0 opacity-0'
        }`}>
          {active.railLabel}
        </span>
      </button>
      {open && (
        <>
          <button
            type="button"
            className="fixed inset-0 z-40 cursor-default"
            aria-label="关闭语言菜单"
            onClick={() => setOpen(false)}
          />
          <div className="absolute bottom-0 left-full z-50 ml-3 w-36 overflow-hidden rounded-2xl border border-black/[0.08] bg-[#fffdfa] p-1.5 shadow-2xl dark:border-white/[0.08] dark:bg-[#211e25]">
            {LANGUAGE_OPTIONS.map(option => (
              <button
                type="button"
                key={option.value}
                onClick={() => {
                  setLanguage(option.value);
                  setOpen(false);
                }}
                className={`flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-[12px] transition ${
                  language === option.value
                    ? 'bg-black/[0.06] text-stone-900 dark:bg-white/[0.1] dark:text-white'
                    : 'text-stone-500 hover:bg-black/[0.035] hover:text-stone-900 dark:text-white/55 dark:hover:bg-white/[0.06] dark:hover:text-white'
                }`}
              >
                <span className="w-4 text-center text-[10px] font-semibold">{option.value === 'zh-CN' ? '中' : 'EN'}</span>
                {option.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function AccountTeamMenu({ user, currentTeam, expanded = false, mobile = false, profilePath, english, preview, refreshUser, refreshTeam }) {
  const navigate = useNavigate();
  const accountTriggerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [switchingId, setSwitchingId] = useState(null);
  const [signingOut, setSigningOut] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const signedIn = Boolean(user);
  const teamPlanActive = Boolean(currentTeam?.subscription_expires_at)
    && new Date(currentTeam.subscription_expires_at).getTime() > Date.now();
  const personalCredits = user?.credits ?? 0;
  const teamCredits = currentTeam?.credits ?? 0;
  const label = signedIn
    ? teamPlanActive
      ? (english ? `Personal credits ${personalCredits}, team credits ${teamCredits}` : `个人点数 ${personalCredits}，团队点数 ${teamCredits}`)
      : (english ? `Credits ${personalCredits}` : `点数余额 ${personalCredits}`)
    : (english ? 'Sign in to Xiaoying' : '登录小影');

  const loadTeams = useCallback(async () => {
    if (!signedIn || preview) {
      setTeams(signedIn ? [{ id: user?.team_id || 1, name: english ? 'My team' : '我的团队', invite_status: 'active', is_current: true }] : []);
      return;
    }
    setLoading(true);
    setFeedback(null);
    try {
      const response = await getTeams();
      setTeams((response?.list || []).filter(team => team.invite_status === 'active'));
    } catch (error) {
      setFeedback({ type: 'error', text: getErrorMessage(teamApiError(error, '团队加载失败')) });
    } finally { setLoading(false); }
  }, [english, preview, signedIn, user?.team_id]);

  useEffect(() => { if (open) loadTeams(); }, [loadTeams, open]);

  const selectTeam = async team => {
    if (preview || team.is_current || String(team.id) === String(user?.team_id) || switchingId) return;
    setSwitchingId(team.id);
    setFeedback(null);
    try {
      await switchTeam(team.id);
      setTeams(items => items.map(item => ({ ...item, is_current: String(item.id) === String(team.id) })));
      await Promise.all([refreshUser?.(), refreshTeam?.()]);
      window.dispatchEvent(new CustomEvent('team-changed', { detail: { teamId: team.id } }));
      setFeedback({ type: 'success', text: english ? `Switched to ${team.name}` : `已切换到「${team.name}」` });
    } catch (error) {
      setFeedback({ type: 'error', text: getErrorMessage(teamApiError(error, '团队切换失败')) });
    } finally { setSwitchingId(null); }
  };

  const signOut = async () => {
    if (signingOut) return;
    if (preview) {
      setFeedback({
        type: 'success',
        text: english ? 'Preview only. This signs you out in the live app.' : '原型预览：正式环境中点击这里会直接退出登录',
      });
      return;
    }
    setSigningOut(true);
    try {
      await api.post('/auth/logout');
    } catch {
      // 即使网络异常也完成本机退出；服务端会话最迟随 JWT 到期。
    }
    localStorage.removeItem('token');
    window.dispatchEvent(new Event('auth-changed'));
    setOpen(false);
    navigate('/home', { replace: true });
  };

  const buttonClass = mobile
    ? 'flex items-center gap-2 rounded-full border border-black/[0.08] bg-[#fffdfa]/92 p-1 pr-3 text-stone-700 shadow-lg backdrop-blur-xl dark:border-white/[0.08] dark:bg-[#211e25]/92 dark:text-white'
    : `flex h-12 w-full min-w-0 items-center rounded-[16px] border border-transparent transition hover:border-black/[0.055] hover:bg-black/[0.035] dark:hover:border-white/[0.06] dark:hover:bg-white/[0.04] ${expanded ? 'px-2' : 'justify-center'}`;
  const avatarClass = mobile
    ? 'flex h-8 w-8 items-center justify-center rounded-full bg-[#e8e1ef] text-[11px] font-semibold text-[#625473] dark:bg-[#332d3a] dark:text-[#d7c9ef]'
    : 'flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-black/[0.09] bg-[#e8e1ef] text-[12px] font-semibold text-[#625473] dark:border-white/[0.1] dark:bg-[#332d3a] dark:text-[#d7c9ef]';

  if (!signedIn) return <NavLink to="/login" className={`${buttonClass}${mobile ? ' fixed left-3 top-3 z-50 sm:hidden' : ''}`} aria-label={label}><UserAvatar user={user} className={avatarClass} />{(mobile || expanded) && <span className={mobile ? 'text-[10px] font-medium' : 'ml-3 text-[12px] font-semibold'}>{english ? 'Sign in' : '登录 / 注册'}</span>}</NavLink>;

  return <div className={mobile ? 'fixed left-3 top-3 z-50 sm:hidden' : 'relative w-full'}>
    <button ref={accountTriggerRef} type="button" onClick={() => setOpen(value => !value)} className={buttonClass} aria-label={label} aria-expanded={open}>
      <UserAvatar user={user} className={avatarClass} />
      {mobile ? <span className="text-[10px] font-medium">{teamPlanActive ? (english ? `Personal ${personalCredits} · Team ${teamCredits}` : `个人 ${personalCredits} · 团队 ${teamCredits}`) : `${personalCredits} ${english ? 'credits' : '点'}`}</span> : <span className={`min-w-0 overflow-hidden text-left transition-all duration-300 ${expanded ? 'ml-3 w-[150px] opacity-100' : 'ml-0 w-0 opacity-0'}`}><span className="block truncate text-[12px] font-semibold text-stone-800 dark:text-white/88">{user?.username}</span><span className="mt-0.5 flex gap-2 whitespace-nowrap text-[9px] text-stone-400 dark:text-white/34"><span>{english ? 'Personal' : '个人'} {personalCredits}</span>{teamPlanActive && <span>{english ? 'Team' : '团队'} {teamCredits}</span>}</span></span>}
    </button>
    {open && <>
      <button type="button" className="fixed inset-0 z-40 cursor-default" aria-label={english ? 'Close account menu' : '关闭账户菜单'} onClick={() => setOpen(false)} />
      <section className={mobile ? 'fixed left-3 top-14 z-50 w-[min(300px,calc(100vw-24px))] rounded-2xl border border-black/[0.08] bg-[#fffdfa] p-2 shadow-2xl dark:border-white/[0.09] dark:bg-[#211e25]' : 'absolute left-full top-0 z-50 ml-3 w-64 rounded-2xl border border-black/[0.08] bg-[#fffdfa] p-2 shadow-2xl dark:border-white/[0.09] dark:bg-[#211e25]'}>
        <div className="flex items-center gap-1">
          <NavLink to={profilePath} onClick={() => { setOpen(false); accountTriggerRef.current?.focus(); }} className="flex min-w-0 flex-1 items-center gap-3 rounded-xl px-2.5 py-2.5 hover:bg-black/[0.035] dark:hover:bg-white/[0.05]"><UserAvatar user={user} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#e8e1ef] text-[12px] font-semibold text-[#625473] dark:bg-[#332d3a] dark:text-[#d7c9ef]" /><span className="min-w-0"><strong className="block truncate text-[13px] text-stone-800 dark:text-white/90">{user.username}</strong><small className="text-[10px] text-stone-400">{english ? 'Account settings' : '个人中心与账户设置'}</small></span></NavLink>
          <button
            type="button"
            onClick={signOut}
            disabled={signingOut}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-[#968ca0] transition hover:bg-[#8c7db0]/[0.10] hover:text-[#75658f] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#9c8bb9]/35 disabled:cursor-wait disabled:opacity-50 dark:text-[#a99fb3] dark:hover:bg-[#b9a7e7]/[0.12] dark:hover:text-[#cbbceb]"
            aria-label={english ? 'Sign out' : '退出登录'}
            title={english ? 'Sign out' : '退出登录'}
          >
            {signingOut ? <LoaderCircle className="animate-spin" size={16} /> : <LogOut size={16} strokeWidth={1.8} />}
          </button>
        </div>
        <div className="mx-2 my-1 border-t border-black/[0.06] dark:border-white/[0.07]" />
        <p className="px-2.5 pb-1 pt-1.5 text-[10px] font-medium text-stone-400">{english ? 'Switch team' : '切换团队'}</p>
        {loading ? <div className="flex items-center gap-2 px-2.5 py-3 text-[11px] text-stone-400"><LoaderCircle className="animate-spin" size={13} />{english ? 'Loading…' : '正在加载…'}</div> : teams.length ? teams.map(team => {
          const current = team.is_current || String(team.id) === String(user?.team_id);
          return <button key={team.id} type="button" onClick={() => selectTeam(team)} disabled={current || Boolean(switchingId)} className={`flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2 text-left transition ${current ? 'bg-black/[0.045] text-stone-900 dark:bg-white/[0.08] dark:text-white' : 'text-stone-600 hover:bg-black/[0.035] dark:text-white/60 dark:hover:bg-white/[0.05]'}`}><span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-black/[0.055] text-[10px] font-semibold dark:bg-white/[0.08]">{String(team.name || '团').charAt(0)}</span><span className="min-w-0 flex-1 truncate text-[12px] font-medium">{team.name}</span>{switchingId === team.id ? <LoaderCircle className="animate-spin" size={13} /> : current ? <Check size={14} /> : <ChevronRight size={13} className="opacity-40" />}</button>;
        }) : <p className="px-2.5 py-3 text-[11px] text-stone-400">{english ? 'No joined teams.' : '暂无已加入团队'}</p>}
        {feedback && <p role={feedback.type === 'error' ? 'alert' : 'status'} className={`mx-1 mt-1 rounded-lg px-2.5 py-2 text-[10px] ${feedback.type === 'error' ? 'bg-red-500/10 text-red-600 dark:text-red-300' : 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'}`}>{feedback.text}</p>}
      </section>
    </>}
  </div>;
}

export default function AppRail({ user, currentTeam = null, theme, setTheme, language = 'zh-CN', setLanguage = () => {}, preview = false, refreshUser, refreshTeam }) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(initialSidebarExpanded);
  const english = language === 'en-US';
  const profilePath = preview ? '/prototype/vnext/account/profile' : '/account/profile';
  const activeBranding = useActiveTeamBranding(currentTeam);
  const teamBrandActive = Boolean(user) && activeBranding;
  const localizeItem = item => ({ ...item, label: english ? item.labelEn : item.label });
  const resolveItem = item => {
    if (!preview) return item;
    if (item.to === '/home') return { ...item, to: '/prototype/vnext' };
    if (item.to === '/writing') return { ...item, to: '/prototype/vnext/writing' };
    if (item.to === '/canvas') return { ...item, to: '/prototype/vnext/canvas' };
    if (item.to === '/assets') return { ...item, to: '/prototype/vnext/assets' };
    if (item.to === '/templates') return { ...item, to: '/prototype/vnext/templates' };
    if (item.to === '/learn') return { ...item, to: '/prototype/vnext/learn' };
    if (item.to === '/help') return { ...item, to: '/prototype/vnext/help' };
    return item;
  };
  const toggleExpanded = () => {
    setExpanded(value => {
      const next = !value;
      try {
        localStorage.setItem('vnext-sidebar-expanded', String(next));
      } catch {
        // The sidebar still works when storage is unavailable.
      }
      return next;
    });
  };
  return (
    <>
      <aside className={`relative z-40 hidden h-screen shrink-0 flex-col border-r border-black/[0.07] bg-[var(--vnext-page)] px-3 py-4 transition-[width] duration-[400ms] ease-[cubic-bezier(0.22,1,0.36,1)] dark:border-white/[0.07] sm:flex ${
        expanded ? 'w-[236px]' : 'w-[76px]'
      }`}>
        <button
          type="button"
          onClick={toggleExpanded}
          className="absolute -right-3 top-[27px] z-10 flex h-6 w-6 items-center justify-center rounded-full border border-black/[0.09] bg-[#fffdfa] text-stone-400 shadow-[0_6px_18px_-10px_rgba(40,35,45,.6)] transition hover:text-stone-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#8c7db0]/45 dark:border-white/[0.1] dark:bg-[#211e25] dark:text-white/42 dark:hover:text-white"
        aria-label={expanded ? (english ? 'Collapse sidebar' : '收起侧边栏') : (english ? 'Expand sidebar' : '展开侧边栏')}
          aria-expanded={expanded}
        >
          {expanded ? <ChevronLeft size={13} /> : <ChevronRight size={13} />}
        </button>

        <NavLink
          to={preview ? '/prototype/vnext' : '/home'}
          className="mb-3 flex h-12 items-center justify-center overflow-visible rounded-[13px] px-2"
          aria-label={teamBrandActive ? `${currentTeam?.brand_name || currentTeam?.name || '团队'}${english ? ' home' : '首页'}` : (english ? 'Xiaoying home' : '小影首页')}
        >
          {teamBrandActive ? <TeamBrandLogo team={currentTeam} expanded={expanded} /> : <span className="whitespace-nowrap text-[23px] font-semibold tracking-[-0.03em]" aria-hidden="true">
            <AnimatedBrandText>{expanded ? 'Xiaoying' : 'Xy'}</AnimatedBrandText>
          </span>}
        </NavLink>

        <AccountTeamMenu user={user} currentTeam={currentTeam} expanded={expanded} profilePath={profilePath} english={english} preview={preview} refreshUser={refreshUser} refreshTeam={refreshTeam} />

        <div className="mx-1 mt-4 border-t border-black/[0.06] dark:border-white/[0.06]" />

        <nav className="mt-4 flex flex-col gap-1.5" aria-label="主要导航">
          {NAV_ITEMS.map(item => <RailLink key={item.to} item={resolveItem(localizeItem(item))} expanded={expanded} />)}
        </nav>

        <div className="mt-auto flex flex-col gap-1.5">
          <RailLink item={{ ...localizeItem(SETTINGS_ITEM), to: profilePath }} expanded={expanded} />
          <RailLink item={resolveItem(localizeItem(HELP_ITEM))} expanded={expanded} />
          <LanguageMenu language={language} setLanguage={setLanguage} expanded={expanded} />
          <SystemMessageTrigger
            className="flex h-10 w-full items-center rounded-[13px] px-3 text-left text-stone-500 transition hover:bg-black/[0.035] hover:text-stone-800 dark:text-white/42 dark:hover:bg-white/[0.045] dark:hover:text-white/78"
            english={english}
            onUnavailable={() => navigate('/login')}
            title={english ? 'My messages' : '通知'}
          >
            <span className={`overflow-hidden whitespace-nowrap text-[12px] font-medium transition-all duration-300 ${
              expanded ? 'ml-3 w-[140px] opacity-100' : 'ml-0 w-0 opacity-0'
            }`}>{english ? 'My messages' : '通知'}</span>
          </SystemMessageTrigger>
          <ThemeMenu theme={theme} setTheme={setTheme} expanded={expanded} english={english} />
        </div>
      </aside>

      <AccountTeamMenu user={user} currentTeam={currentTeam} mobile profilePath={profilePath} english={english} preview={preview} refreshUser={refreshUser} refreshTeam={refreshTeam} />

      <SystemMessageTrigger
        className="fixed right-3 top-3 z-50 flex h-10 w-10 items-center justify-center rounded-full border border-black/[0.08] bg-[#fffdfa]/92 text-stone-600 shadow-lg backdrop-blur-xl transition hover:text-stone-900 dark:border-white/[0.08] dark:bg-[#211e25]/92 dark:text-white/65 dark:hover:text-white sm:hidden"
        english={english}
        onUnavailable={() => navigate('/login')}
        title={english ? 'My messages' : '通知'}
      >
      </SystemMessageTrigger>

      <nav className="fixed inset-x-3 bottom-3 z-50 flex items-center rounded-[22px] border border-black/[0.08] bg-[#fffdfa]/94 px-2 py-1.5 shadow-2xl backdrop-blur-xl dark:border-white/[0.08] dark:bg-[#211e25]/94 sm:hidden" aria-label="移动端主要导航">
        {NAV_ITEMS.map(item => <RailLink key={item.to} item={resolveItem(localizeItem(item))} mobile />)}
        <RailLink item={resolveItem(localizeItem(HELP_ITEM))} mobile />
      </nav>

    </>
  );
}
